import type {
    DraggableAttributes,
    DraggableSyntheticListeners,
} from '@dnd-kit/core';
import type { ImAccount, MoneyMapRuleWithAccount } from '@fruitful/types';
import React from 'react';
import {
    AlertOctagonIcon,
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CirclePlusIcon,
    GripVerticalIcon,
    hitSlop,
    Icon,
    SquarePenIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import {
    type BankAccountDisplayDetail,
    type DepositAccount,
    type IncomeSplitReviewContext,
    type IncomeSplitRule,
    type IncomeSplitRuleSet,
    IncomeSplitRuleType,
    type PaycheckRoutingRuleAccountMetadata,
} from '../types/IncomeSplit';
import { getIncomeSplitAccountVisuals } from '../utils/incomeSplitAccountUtils';
import {
    getAccountFromRuleNew,
    isBackdoorTradRule,
} from '../utils/incomeSplitUtilsNew';
import IncomeSplitRuleCard from './IncomeSplitRuleCardNew';

export const IncomeSplitRuleSetCard = ({
    group,
    bankAccountDetails,
    externalAccounts,
    moneyMapRulesWithProposedAccounts,
    internalInvestmentAccounts,
    context,
    setSelectedRule,
    onAddRule,
    onEditRule,
    onPressEdit,
    drag,
    setActivatorNodeRef,
    listeners,
    attributes,
    onDragHandlePressIn,
    onDragHandlePressOut,
    showSingleRuleCard,
}: {
    group: IncomeSplitRuleSet;
    bankAccountDetails:
        | PaycheckRoutingRuleAccountMetadata[]
        | BankAccountDisplayDetail[]
        | DepositAccount[];
    externalAccounts?: DepositAccount[];
    moneyMapRulesWithProposedAccounts?: MoneyMapRuleWithAccount[];
    internalInvestmentAccounts?: ImAccount[];
    context: IncomeSplitReviewContext;
    setSelectedRule: (rule: IncomeSplitRule | Partial<IncomeSplitRule>) => void;
    onAddRule?: (rule: Partial<IncomeSplitRule>) => void;
    onEditRule?: () => void;
    onPressEdit?: () => void;
    drag?: () => void;
    listeners?: DraggableSyntheticListeners;
    attributes?: DraggableAttributes;
    setActivatorNodeRef?: (element: HTMLElement | null) => void;
    onDragHandlePressIn?: () => void;
    onDragHandlePressOut?: () => void;
    showSingleRuleCard?: boolean;
}) => {
    const { role: _omitRole, ...safeAttributes } = attributes ?? {};

    const { rules, name } = group;
    const sortedRules = (rules ?? []).sort((a, b) => a.priority - b.priority);

    const totalRulesPercentage = (rules ?? []).reduce(
        (sum, r) => sum + (r.percentage ?? 0),
        0,
    );

    return (
        <YStack
            backgroundColor="$gray200"
            borderRadius={24}
            padding={16}
            gap={12}
        >
            <XStack alignItems="center" justifyContent="space-between">
                <BodyMediumRegular flex={1}>{name}</BodyMediumRegular>
                <XStack alignItems="center" gap={8}>
                    <View
                        alignItems="center"
                        justifyContent="center"
                        animation="button"
                        pressStyle={{ scale: 0.95 }}
                        onPress={onPressEdit}
                        cursor="pointer"
                        width={16}
                        height={16}
                        {...hitSlop(12)}
                    >
                        <Icon
                            icon={SquarePenIcon}
                            size={16}
                            color="$gray1200"
                        />
                    </View>
                    <View
                        ref={setActivatorNodeRef}
                        {...(listeners ?? {})}
                        {...safeAttributes}
                        alignItems="center"
                        justifyContent="center"
                        cursor="grab"
                        width={16}
                        height={16}
                        {...hitSlop(12)}
                        onMouseDown={onDragHandlePressIn}
                        onMouseUp={onDragHandlePressOut}
                        onPressIn={onDragHandlePressIn}
                        onPressOut={onDragHandlePressOut}
                        onLongPress={drag}
                    >
                        <Icon
                            icon={GripVerticalIcon}
                            size={16}
                            color="$gray1200"
                        />
                    </View>
                </XStack>
            </XStack>
            <View gap={8}>
                {sortedRules.map((rule) => {
                    const account = getAccountFromRuleNew({
                        rule,
                        bankAccountDetails,
                        externalAccounts: externalAccounts ?? [],
                        moneyMapRulesWithProposedAccounts:
                            moneyMapRulesWithProposedAccounts ?? [],
                        internalInvestmentAccounts:
                            internalInvestmentAccounts ?? [],
                    });
                    if (!account) return null;

                    const { color, icon } =
                        getIncomeSplitAccountVisuals(account);

                    const isBackdoorTrad = isBackdoorTradRule({
                        rule,
                        internalInvestmentAccounts,
                        moneyMapRulesWithProposedAccounts,
                    });

                    return (
                        <IncomeSplitRuleCard
                            key={rule.id}
                            rule={rule}
                            account={account}
                            color={color}
                            icon={icon}
                            context={context}
                            setSelectedRule={setSelectedRule}
                            onPressEdit={onEditRule}
                            isBackdoorTrad={isBackdoorTrad}
                        />
                    );
                })}
            </View>
            {showSingleRuleCard && (
                <YStack
                    padding={12}
                    gap={8}
                    backgroundColor="$orange300"
                    borderRadius={8}
                >
                    <XStack gap={8} alignItems="center">
                        <Icon
                            icon={AlertOctagonIcon}
                            color="$orange1100"
                            size={16}
                        />
                        <BodySmallEmphasized color="$orange1100">
                            Income Split stops here
                        </BodySmallEmphasized>
                    </XStack>
                    <BodySmallRegular color="$orange1100">
                        This rule doesn&apos;t include a target or limit, so all
                        remaining funds will stop here and won&apos;t contribute
                        to subsequent rules.
                    </BodySmallRegular>
                </YStack>
            )}
            {rules.length && totalRulesPercentage !== 100 ? (
                <View
                    paddingHorizontal={16}
                    paddingVertical={12}
                    backgroundColor="$red200"
                    gap={8}
                    borderRadius={8}
                >
                    <BodyMediumRegular color="$red1100">
                        Rules total {totalRulesPercentage}%. Combined rule
                        contributions within a rule set must total 100%.
                    </BodyMediumRegular>
                </View>
            ) : null}
            <Button
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.LARGE}
                text="Add rule"
                icon={CirclePlusIcon}
                onPress={() => {
                    if (!onAddRule) return;
                    onAddRule({
                        incomeSplitConfigId: group.incomeSplitConfigId,
                        incomeSplitRuleSetId: group.id,
                        type: IncomeSplitRuleType.PERCENTAGE,
                        priority: group.rules.length ?? 1,
                        percentage: undefined,
                    });
                }}
            />
        </YStack>
    );
};

export default IncomeSplitRuleSetCard;
